"use client";

import { useLayoutEffect, useRef } from "react";
import { MarqueeController } from "@/components/behaviors";
import styles from "./ClientShowcase.module.css";
import type { ClientShowcaseProps, ShowcaseTile } from "./ClientShowcase.types";

function Tile({ tile }: { tile: ShowcaseTile }) {
  const hasOverlay = Boolean(tile.name || tile.description || tile.result || (tile.tags && tile.tags.length));
  const hasDetails = Boolean(tile.description || tile.result);
  const content = (
    <>
      <img className={styles.tileImage} data-fit={tile.image.fit} src={tile.image.src} alt={tile.image.alt} loading="lazy" />
      {hasOverlay ? (
        <div className={styles.overlay} data-persistent={Boolean(tile.name || tile.tags?.length)} data-result={Boolean(tile.result)}>
          {tile.name ? <h3 className={styles.tileName}>{tile.name}</h3> : null}
          {tile.description ? <div className={styles.details}><p className={styles.tileText}>{tile.description}</p></div> : null}
          {tile.result ? <p className={styles.tileResult}>{tile.result}</p> : null}
          {tile.tags && tile.tags.length ? (
            <ul className={styles.tags}>
              {tile.tags.map((tag) => (
                <li key={tag} className={styles.tag}>
                  {tag}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {tile.pulse ? <span className={styles.pulse} aria-hidden="true" /> : null}
    </>
  );
  return (
    <div className={styles.tile} data-size={tile.size ?? "small"} tabIndex={!tile.href && hasDetails ? 0 : undefined}>
      {tile.href ? (
        <a href={tile.href} className={styles.tileLink}>
          {content}
        </a>
      ) : (
        content
      )}
    </div>
  );
}

function ArrowIcon({ direction }: { direction: "prev" | "next" }) {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {direction === "prev" ? <path d="M15 5l-7 7 7 7" /> : <path d="M9 5l7 7-7 7" />}
    </svg>
  );
}

/** Variante slider : une rangée de cartes uniformes (une carte = une photo), défilement à la main avec accroche. */
function ShowcaseSlider({ tiles }: { tiles: ReadonlyArray<ShowcaseTile> }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollByCard = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>("[data-size]");
    const step = card ? card.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollBy({ left: direction * step, behavior: reduced ? "auto" : "smooth" });
  };
  return (
    <div className={styles.slider}>
      <div className={styles.sliderTrack} ref={trackRef} data-showcase-track="" data-variant="slider">
        {tiles.map((tile, i) => (
          <Tile key={i} tile={{ ...tile, size: "tall" }} />
        ))}
      </div>
      <div className={styles.sliderNav}>
        <button type="button" className={styles.sliderButton} onClick={() => scrollByCard(-1)} aria-label="Réalisations précédentes">
          <ArrowIcon direction="prev" />
        </button>
        <button type="button" className={styles.sliderButton} onClick={() => scrollByCard(1)} aria-label="Réalisations suivantes">
          <ArrowIcon direction="next" />
        </button>
      </div>
    </div>
  );
}

/** Mosaïque de réalisations : grille en colonnes (tuiles hautes et basses) qui défile en boucle. */
export function ShowcaseGallery({ tiles, variant = "marquee", speed = 60 }: NonNullable<ClientShowcaseProps["gallery"]>) {
  const hostRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || variant !== "marquee") return;
    const controller = new MarqueeController(host, { rowSelector: "[data-showcase-track]", speed, keepLayout: true });
    return () => controller.destroy();
  }, [tiles, variant, speed]);
  if (variant === "slider") return <ShowcaseSlider tiles={tiles} />;
  const groups: ShowcaseTile[][] = [];
  for (const tile of tiles) {
    const previous = groups.at(-1);
    if (tile.client && previous?.[0].client === tile.client) previous.push(tile);
    else groups.push([tile]);
  }
  return (
    <div className={styles.gallery} ref={hostRef}>
      <div className={styles.track} data-showcase-track="" data-variant={variant}>
        {groups.map((group, i) => (
          <div key={i} className={styles.clientGroup} data-client={group[0].client}>
            {group.map((tile, j) => <Tile key={j} tile={tile} />)}
          </div>
        ))}
      </div>
    </div>
  );
}
